<script setup>
import BadgeShape from "@/Shared/BadgeShape";
import RainbooIcon from "@/Shared/SVG/RainbooIcon";
import BlogCard from "@/Shared/BlogCard";
import { Navigation, Pagination} from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/vue/24/solid';
import { isEmpty } from 'lodash';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import FacebookShareIcon from "@/Shared/SVG/FacebookShareIcon";
import TwitterShareIcon from "@/Shared/SVG/TwitterShareIcon";
import LinkedinShareIcon from "@/Shared/SVG/LinkedinShareIcon";
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { MinusSmallIcon, PlusSmallIcon } from '@heroicons/vue/24/outline'

let props = defineProps({
    blog: Object,
    relatedBlogs: Object,
    faqs: Object,
});

let blog = props.blog.data
let relatedBlogs = props.relatedBlogs.data
let faqs = props.faqs.data
let currentUrl = window.location.href
</script>

<template>
    <div class="fixed left-1/12 lg:left-1/6 top-1/3 xl:top-2/4 z-50 py-3 bg-transparent hidden md:flex flex-col space-y-3">
        <span class="text-white text-xs m-auto">Share</span>
        <ShareNetwork
            network="facebook"
            :url="currentUrl"
            :title="blog.title"
            class="border border-white border-opacity-20 rounded-full p-3"
        >
            <FacebookShareIcon class="h-6 w-6" />
        </ShareNetwork>
        <ShareNetwork
            network="twitter"
            :url="currentUrl"
            :title="blog.title"
            class="border border-white border-opacity-20 rounded-full p-4"
        >
            <TwitterShareIcon class="h-4 w-4" />
        </ShareNetwork>
        <ShareNetwork
            network="LinkedIn"
            :url="currentUrl"
            :title="blog.title"
            class="border border-white border-opacity-20 rounded-full p-4"
        >
            <LinkedinShareIcon class="h-4 w-4"/>
        </ShareNetwork>
    </div>

    <div class="w-full relative">
        <h1 class="text-white text-3xl font-bold mt-[71px] mb-[35px] sm:mb-[75px] text-center px-2">{{blog.title}}</h1>
        <article class="mx-0 mb-10">
            <div class="grid grid-cols-12">
                <div class="aspect-w-[1000] aspect-h-[500] mt-5 col-start-2 col-span-10">
                    <img class="object-cover rounded-2xl" :src="blog.cover_url" :alt="blog.title">
                </div>
            </div>
            <div class="grid grid-cols-12 mt-10">
                <div class="col-span-8 col-start-3">
                    <p class="max-w-full text-white prose prose-white" v-html="blog.content"></p>
                    <div class="mt-14" v-if="!isEmpty(blog.blogTags)">
                        <span class="text-white text-opacity-60 text-xs">TAGS</span>
                        <div class="mt-4 flex space-x-1">
                            <BadgeShape v-for="blogTag in blog.blogTags" type="block" class="w-fit text-xs text-white text-opacity-60 border border-white border-opacity-60 rounded bg-transparent  p-1">{{blogTag.tag}}</BadgeShape>
                        </div>
                    </div>
                </div>
            </div>
        </article>
        <div v-if="Object.keys(faqs).length">
          <div class="mt-40 w-full border-b border-wgn-purple-4">
            <RainbooIcon class="m-auto"/>
          </div>
          <h4 class="text-xl text-white mt-3 text-center">Frequently asked questions</h4>
          <div class="flex flex-col mt-6 sm:mt-10 relative max-w-6xl text-white m-auto">
            <dl class="mt-10 space-y-6 divide-y divide-white/10">
              <Disclosure as="div" v-for="faq in faqs" :key="faq.id" class="pt-6" v-slot="{ open }">
                <dt>
                  <DisclosureButton class="flex w-full items-start justify-between text-left text-white">
                    <span class="text-white font-semibold leading-7">{{ faq.question }}</span>
                    <span class="ml-6 flex h-7 items-center">
                  <PlusSmallIcon v-if="!open" class="h-6 w-6" aria-hidden="true" />
                  <MinusSmallIcon v-else class="h-6 w-6" aria-hidden="true" />
                </span>
                  </DisclosureButton>
                </dt>
                <DisclosurePanel as="dd" class="mt-2 pr-12">
                  <p class="text-base leading-7 text-gray-300">{{ faq.answer }}</p>
                </DisclosurePanel>
              </Disclosure>
            </dl>
          </div>
        </div>
        <div v-if="Object.keys(relatedBlogs).length">
            <div class="mt-40 w-full border-b border-wgn-purple-4">
                <RainbooIcon class="m-auto"/>
            </div>
            <h4 class="text-xl text-white mt-3 text-center">Similar Articles</h4>
            <div class="flex flex-col mt-6 sm:mt-28 relative">
                <swiper class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 w-full"
                        :breakpoints="{
                            1: {slidesPerView:1},
                            640: {slidesPerView:2},
                            1024: {slidesPerView:3}
                        }"
                        :space-between="16"
                        :loop="(Object.keys(relatedBlogs).length > 1)?true:false"
                        :pagination="{
                          el: '.blog_pagination',
                          clickable: true,
                        }"
                        paginationClickable: true
                        :modules="[Pagination]"
                >

                    <!--                        :navigation= "{-->
                    <!--                            nextEl: '.blog_swiper_button_next',-->
                    <!--                            prevEl: '.blog_swiper_button_prev'-->
                    <!--                        }"-->
                    <swiper-slide v-for="relatedBlog in relatedBlogs">
                        <BlogCard :blog="relatedBlog" :responsiveChangeStyle="false"></BlogCard>
                    </swiper-slide>
                </swiper>
                <div v-if="0" class="blog_swiper_button_prev">
                    <div class="flex h-4 w-4 rounded-full text-sm text-white items-center bg-white">
                        <ArrowLeftIcon class="text-gray-700 text-center mr-0.5 w-full m-auto"></ArrowLeftIcon>
                    </div>
                </div>
                <div class="blog_pagination text-center" slot="pagination"></div>
                <div v-if="0" class="absolute blog_swiper_button_next">
                    <div class="rounded-full bg-white rounded-full w-8 h-8">
                        <ArrowRightIcon class="text-black h-full w-4 m-auto"></ArrowRightIcon>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
